import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { themeCategories } from '../data/themeSuggestions';
import './ThemePicker.css';

interface ThemePickerProps {
  inputId: string;
  label: string;
  value: string;
  onChange: (theme: string) => void;
  variant?: 'inline' | 'popover';
}

const ThemePicker: React.FC<ThemePickerProps> = ({ inputId, label, value, onChange, variant = 'inline' }) => {
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState(themeCategories[0].id);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState({ top: 16, left: 16, maxHeight: 520 });
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const tilesRef = useRef<HTMLDivElement>(null);
  const isPopover = variant === 'popover';
  const panelId = `${inputId}-suggestions`;
  const search = query.trim().toLowerCase();
  const visibleThemes = themeCategories
    .filter(category => search || category.id === categoryId)
    .flatMap(category => category.themes)
    .filter(theme => !search || theme.toLowerCase().includes(search));

  const closePicker = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (open) searchRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (tilesRef.current) tilesRef.current.scrollTop = 0;
  }, [categoryId, query]);

  useLayoutEffect(() => {
    if (!open) return;

    // Account for both the scrolling results dialog and the mobile on-screen keyboard.
    const viewport = window.visualViewport;
    const updatePosition = () => {
      const anchor = triggerRef.current?.getBoundingClientRect();
      const panel = panelRef.current?.getBoundingClientRect();
      if (!anchor || !panel) return;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportTop = viewport?.offsetTop ?? 0;
      const maxHeight = Math.min(520, viewportHeight - 32);
      const panelHeight = Math.min(panel.height, maxHeight);
      const above = anchor.top - panelHeight - 8;
      const below = anchor.bottom + 8;
      const preferredTop = above >= viewportTop + 16 ? above : below;
      setPosition({
        top: Math.max(viewportTop + 16, Math.min(preferredTop, viewportTop + viewportHeight - panelHeight - 16)),
        left: Math.max(16, Math.min(anchor.right - panel.width, window.innerWidth - panel.width - 16)),
        maxHeight,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    viewport?.addEventListener('resize', updatePosition);
    viewport?.addEventListener('scroll', updatePosition);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePosition) : undefined;
    if (panelRef.current) observer?.observe(panelRef.current);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      viewport?.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('scroll', updatePosition);
      observer?.disconnect();
    };
  }, [open, categoryId, query]);

  const selectTheme = (theme: string) => {
    onChange(theme);
    setOpen(false);
    inputRef.current?.focus({ preventScroll: true });
  };

  const picker = (
    <div
      ref={panelRef}
      id={panelId}
      className={`theme-picker${isPopover ? ' theme-picker--popover' : ''}`}
      style={isPopover ? position : undefined}
      role={isPopover ? 'dialog' : 'region'}
      aria-modal={isPopover ? true : undefined}
      aria-labelledby={`${panelId}-title`}
      onKeyDown={event => {
        if (!isPopover) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closePicker();
        }
        if (event.key === 'Tab') {
          const controls = panelRef.current?.querySelectorAll<HTMLElement>('button, input');
          if (!controls?.length) return;
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }}
    >
      <div className="theme-picker-heading">
        <div>
          <h3 id={`${panelId}-title`}><span aria-hidden="true">✦</span> {isPopover ? 'Choose a theme' : 'Need inspiration?'}</h3>
          <p>Pick an idea, then make it your own.</p>
        </div>
        {isPopover && (
          <button type="button" className="btn btn-ghost btn-icon btn-icon--sm" aria-label="Close theme picker" onClick={closePicker}>
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
      <input
        ref={searchRef}
        type="search"
        className="input theme-picker-search"
        aria-label="Search themes"
        placeholder="Search all themes…"
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
      <div className="theme-picker-categories" role="group" aria-label="Theme categories">
        {themeCategories.map(category => (
          <button
            key={category.id}
            type="button"
            className="theme-category"
            aria-pressed={!search && categoryId === category.id}
            onClick={() => { setCategoryId(category.id); setQuery(''); }}
          >
            {category.label}
          </button>
        ))}
      </div>
      <p className="theme-picker-count" role="status">
        {visibleThemes.length} {visibleThemes.length === 1 ? 'idea' : 'ideas'}{search ? ' across all categories' : ' to get you started'}
      </p>
      <div ref={tilesRef} className="theme-picker-tiles">
        {visibleThemes.map(theme => (
          <button
            key={theme}
            type="button"
            className="theme-tile"
            aria-pressed={value === theme}
            onClick={() => selectTheme(theme)}
          >
            <span>{theme}</span>
            <span className="theme-tile-mark" aria-hidden="true">{value === theme ? '✓' : '+'}</span>
          </button>
        ))}
        {visibleThemes.length === 0 && (
          <p className="theme-picker-empty">No themes found. Try another search, or write your own in the theme field.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="field theme-field">
      <label className="field-label" htmlFor={inputId}>{label} <span className="muted">(optional)</span></label>
      <input
        ref={inputRef}
        className="input"
        type="text"
        id={inputId}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder="e.g. 80s hits, movie soundtracks, one-hit wonders"
        aria-describedby={`${inputId}-hint`}
      />
      <p className="field-hint" id={`${inputId}-hint`}>Everyone will be asked to pick a song that fits. You can edit any suggestion.</p>
      {isPopover ? (
        <>
          <button
            ref={triggerRef}
            type="button"
            className="btn btn-secondary btn-sm theme-picker-trigger"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            onClick={() => setOpen(true)}
          >
            <span aria-hidden="true">✦</span> Browse themes
          </button>
          {open && createPortal(
            <div className="theme-picker-overlay" onMouseDown={event => {
              if (event.target === event.currentTarget) closePicker();
            }}>
              {picker}
            </div>,
            document.body,
          )}
        </>
      ) : picker}
    </div>
  );
};

export default ThemePicker;
